import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * SiteContext — global active-site selector + access helpers.
 *
 * Behavior:
 *  • Owners (and managers with empty site_ids) see all sites + can pick "All sites"
 *  • Scoped users see only their assigned sites; cannot pick "All sites"
 *  • Single-site users are locked to their one site (no selector)
 *
 * The active site id is persisted in localStorage as `vb_active_site`
 * (or the literal string "all" for the all-sites view). The api.js
 * axios interceptor reads from there to set the `X-Site-Context` header.
 */
const SiteCtx = createContext(null);

export function SiteProvider({ children }) {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeSiteId, setActiveSiteIdState] = useState(() => {
    const s = localStorage.getItem("vb_active_site");
    return s || "all";
  });

  const role = user?.role;
  const userSiteIds = user?.site_ids || [];
  const seesAll =
    role === "owner" ||
    role === "super_admin" ||
    (role === "manager" && userSiteIds.length === 0);

  // Load tenant sites once authenticated.
  useEffect(() => {
    if (!user || role === "super_admin") {
      setSites([]); setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/sites");
        if (!cancelled) {
          setSites(data.sites || []);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) { setSites([]); setLoaded(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [user, role]);

  // List of sites this user is allowed to see.
  const visibleSites = useMemo(() => {
    if (seesAll) return sites;
    return sites.filter((s) => userSiteIds.includes(s.id));
  }, [sites, seesAll, userSiteIds]);

  const isSingleSite = !seesAll && visibleSites.length === 1;
  const canPickAllSites = seesAll;

  // Reconcile the persisted active site against current visibility.
  useEffect(() => {
    if (!loaded) return;
    // If we have no visible sites yet (e.g. transient auth state), don't reset.
    if (visibleSites.length === 0) return;
    if (isSingleSite) {
      const only = visibleSites[0]?.id;
      if (only && activeSiteId !== only) {
        localStorage.setItem("vb_active_site", only);
        setActiveSiteIdState(only);
      }
      return;
    }
    if (activeSiteId === "all" && !canPickAllSites) {
      const fallback = visibleSites[0]?.id;
      if (fallback) {
        localStorage.setItem("vb_active_site", fallback);
        setActiveSiteIdState(fallback);
      }
      return;
    }
    if (activeSiteId !== "all" && !visibleSites.some((s) => s.id === activeSiteId)) {
      const fallback = canPickAllSites ? "all" : visibleSites[0]?.id;
      if (fallback) {
        localStorage.setItem("vb_active_site", fallback);
        setActiveSiteIdState(fallback);
      }
    }
  }, [loaded, visibleSites, activeSiteId, canPickAllSites, isSingleSite]);

  const setActiveSite = useCallback((id) => {
    const value = id || "all";
    localStorage.setItem("vb_active_site", value);
    setActiveSiteIdState(value);
  }, []);

  const value = useMemo(
    () => ({
      loaded,
      sites,
      visibleSites,
      activeSiteId,
      setActiveSite,
      isAllSites: activeSiteId === "all",
      isSingleSite,
      canPickAllSites,
      activeSite: visibleSites.find((s) => s.id === activeSiteId) || null,
      hasNoSiteAssigned:
        !!user && !seesAll && (visibleSites.length === 0),
    }),
    [loaded, sites, visibleSites, activeSiteId, setActiveSite, isSingleSite, canPickAllSites, user, seesAll]
  );

  return <SiteCtx.Provider value={value}>{children}</SiteCtx.Provider>;
}

export function useSite() {
  const v = useContext(SiteCtx);
  if (!v) throw new Error("useSite must be used inside SiteProvider");
  return v;
}
